/**
 * Вкладення документа (рішення власника 2026-08-12).
 *
 * Панель «📎 вкладення (N)» для editor'ів документів: список файлів
 * (відкриття у новій вкладці, видалення) + кнопка «прикріпити файл».
 * Приймаються PDF/JPEG/PNG/HEIC до 10 МБ; сервер стискає (фото → ~1МБ)
 * і зберігає лише стиснуту версію. Показуємо стиснутий розмір.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { attachments, type Attachment } from '@/api/attachments';
import { getMe } from '@/api/auth';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { signControl } from '@/api/signControl';

/** ч.852: іконки дій вкладення — у стилі кнопки «створити з замовлення». */
function IconSend() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M14.5 1.5 7.5 8.5M14.5 1.5l-4.4 13-2.6-6-6-2.6 13-4.4Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 1.5v8.5m0 0L4.8 6.8M8 10l3.2-3.2M2 12.5v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconPaperclip() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M13 7.5 8 12.5a3 3 0 0 1-4.2-4.2l5.4-5.4a2 2 0 0 1 2.8 2.8l-5.4 5.4a1 1 0 0 1-1.4-1.4l4.7-4.7"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AttachmentsPanel({ organizationId, documentType, documentId }: {
  organizationId: string;
  documentType: string;           // 'doc.goods_receipts' тощо
  documentId: string | undefined; // undefined/new — панель прихована
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  // 2026-08-13 (рішення власника): клік → ПРЕДПРОСМОТР з кнопками
  // «завантажити»/«друкувати» (замість відкриття у новій вкладці).
  const [preview, setPreview] = useState<{ att: Attachment; url: string } | null>(null);
  // Уніфікація відправки (рішення власника 2026-08-13): ЦЕНТР ВІДПРАВКИ —
  // Журнал ЕДО. «надіслати» у рядку лише ДОДАЄ документ у журнал
  // («не відправлено»); email/підпис КЕП — дії ВЖЕ У ЖУРНАЛІ.
  const [sending, setSending] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Статус ЕЦП з журналу ЕДО (рішення власника 2026-08-13): вкладення,
  // відправлене на підпис, отримує бейдж «ЕЦП» + статус + «скачати»
  // підписаний контрагентом p7s.
  interface EdoDocRow {
    id: string;
    attachment_id: string | null;
    subject: string | null;
    sign_status: string | null;
    own_signed: boolean;
    signed_attachment_id: string | null;
    signer_info: string | null;
  }
  const edoQ = useQuery({
    queryKey: ['edo-journal-doc', documentType, documentId],
    // /edo/journal тепер віддає конверт {items,total,...} (2026-08-22).
    queryFn: () => api.get<{ items: EdoDocRow[] }>('/edo/journal', {
      params: { organization_id: organizationId, document_type: documentType,
                document_id: documentId, limit: 100 },
    }).then((r) => r.data.items),
    // НЕ 'new' (інакше 422 на кожному НОВОМУ документі — document_id UUID).
    enabled: !!documentId && documentId !== 'new' && !!organizationId,
    refetchInterval: 60_000,
  });
  const edoByAtt = useMemo(() => {
    const m = new Map<string, EdoDocRow>();
    for (const e of edoQ.data ?? []) {
      if (!e.attachment_id || (!e.sign_status && !e.own_signed)) continue;
      const prev = m.get(e.attachment_id);
      // Пріоритет: SIGNED > WAITING > лише власний підпис.
      if (!prev || e.sign_status === 'SIGNED'
          || (e.sign_status === 'WAITING' && prev.sign_status !== 'SIGNED')) {
        m.set(e.attachment_id, e);
      }
    }
    return m;
  }, [edoQ.data]);
  // Скачування підписаного — 3 формати (рішення власника 2026-08-14).
  const [dlEdo, setDlEdo] = useState<{ row: EdoDocRow; base: string } | null>(null);
  const downloadEdoFormat = async (row: EdoDocRow, base: string,
                                   format: 'pdf' | 'p7s' | 'zip') => {
    try {
      const resp = await api.get(`/edo/journal/${row.id}/download`, {
        params: { format }, responseType: 'blob' });
      const name = format === 'zip' ? `${base} (підписано).zip`
        : format === 'p7s' ? `${base} (підпис контрагента).p7s`
        : base;
      const url = URL.createObjectURL(resp.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setDlEdo(null);
    } catch {
      window.alert('Не вдалося скачати файл');
    }
  };
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const openPreview = async (att: Attachment) => {
    const blob = await attachments.downloadBlob(att.id);
    setPreview({ att, url: URL.createObjectURL(blob) });
    setOverlays([]);
    setEditMode(false);
    setPdfPage(1);
    setPdfPages(0);
    setPdfPageUrl(null);
    if (att.mime_type === 'application/pdf') {
      attachments.pdfInfo(att.id).then((i) => setPdfPages(i.pages)).catch(() => {});
    }
  };
  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setOverlays([]);
    setEditMode(false);
  };

  // ===== Редактор печатки/підпису (рішення власника 2026-08-13) =====
  // Лише для зображень (PDF — наступна фаза, TD-STAMP-PDF).
  interface Overlay {
    kind: string; attId: string; url: string;
    x: number; y: number; w: number; page: number;
  }
  const [editMode, setEditMode] = useState(false);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [saving, setSaving] = useState(false);
  // PDF-режим (2026-08-13): рендер сторінки з бека → та сама drag-механіка.
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfPageUrl, setPdfPageUrl] = useState<string | null>(null);
  const docImgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ idx: number; dx: number; dy: number } | null>(null);

  // ПРАВА ГРУП печаток (рішення власника 2026-08-13, перевірка у редакторі):
  // company = печатка ПІДПРИЄМСТВА + підпис КЕРІВНИКА (головбух);
  // docs    = печатка «для документів» + ВЛАСНИЙ підпис (бухгалтер/менеджери).
  const permCompany = usePermissionsByOrg('documents.stamps_company.use');
  const permDocs = usePermissionsByOrg('documents.stamps_docs.use');
  // ч.1315: у журнал ЕДО — лише з правом надсилання в організації документа.
  const permEdoSend = usePermissionsByOrg('edo.send.manage');
  const canEdoSend = permEdoSend.availableOrgs.some((o) => o.id === organizationId);
  const canCompany = permCompany.availableOrgs.some((o) => o.id === organizationId);
  const canDocs = permDocs.availableOrgs.some((o) => o.id === organizationId);

  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: !!preview });

  const mkStampQuery = (type: string, docId: string | undefined) => ({
    queryKey: ['attachments', type, docId, organizationId],
    queryFn: () => attachments.list({
      organization_id: organizationId, document_type: type,
      document_id: docId! }),
    enabled: !!organizationId && !!preview && !!docId,
  });
  const stampQ = useQuery(mkStampQuery('org.stamp', organizationId));
  const stampDocsQ = useQuery(mkStampQuery('org.stamp_docs', organizationId));
  const signQ = useQuery(mkStampQuery('org.signature', organizationId));
  const userSignQ = useQuery(mkStampQuery('user.signature', meQ.data?.id));

  // Доступні кнопки: (право групи) ∧ (штамп завантажено).
  const stampButtons: { kind: string; label: string; list?: Attachment[] }[] = [
    ...(canCompany ? [
      { kind: 'org.stamp', label: '+ печатка підприємства', list: stampQ.data },
      { kind: 'org.signature', label: '+ підпис керівника', list: signQ.data },
    ] : []),
    ...(canDocs ? [
      { kind: 'org.stamp_docs', label: '+ штамп для документів', list: stampDocsQ.data },
      { kind: 'user.signature', label: '+ мій підпис', list: userSignQ.data },
    ] : []),
  ].filter((b) => (b.list?.length ?? 0) > 0);
  const hasStamps = stampButtons.length > 0;

  const addOverlay = async (kind: string, list?: Attachment[]) => {
    const att = list?.[list.length - 1];
    if (!att) return;
    const blob = await attachments.downloadBlob(att.id);
    setOverlays((o) => [...o, {
      kind, attId: att.id, url: URL.createObjectURL(blob),
      x: 40 + o.length * 30, y: 40 + o.length * 30, w: 140,
      page: pdfPage,
    }]);
    setEditMode(true);
  };

  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    if (preview?.att.mime_type === 'application/pdf' && editMode) {
      attachments.pageImage(preview.att.id, pdfPage).then((b) => {
        if (!alive) return;
        url = URL.createObjectURL(b);
        setPdfPageUrl(url);
      });
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.att.id, editMode, pdfPage]);

  const onDragStart = (idx: number, e: React.PointerEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragRef.current = { idx, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const host = docImgRef.current?.parentElement;
    if (!host) return;
    const hostRect = host.getBoundingClientRect();
    setOverlays((o) => o.map((ov, i) => i === d.idx
      ? { ...ov,
          x: Math.max(0, e.clientX - hostRect.left - d.dx),
          y: Math.max(0, e.clientY - hostRect.top - d.dy) }
      : ov));
  };
  const onDragEnd = () => { dragRef.current = null; };

  // Зберегти: канвас natural-розміру, оверлеї масштабуються від показаного.
  const saveStamped = async () => {
    const img = docImgRef.current;
    if (!img || !preview) return;
    setSaving(true);
    try {
      if (preview.att.mime_type === 'application/pdf') {
        // PDF: координати у ДОЛЯХ показаної сторінки → бек накладає.
        await attachments.stampPdf(preview.att.id, overlays.map((ov) => ({
          stamp_attachment_id: ov.attId,
          page: ov.page,
          x_frac: ov.x / img.clientWidth,
          y_frac: ov.y / img.clientHeight,
          w_frac: ov.w / img.clientWidth,
        })));
        qc.invalidateQueries({ queryKey: ['attachments', documentType, documentId] });
        closePreview();
        return;
      }
      const scale = img.naturalWidth / img.clientWidth;
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      for (const ov of overlays) {
        const el = new Image();
        el.src = ov.url;
        await el.decode();
        const w = ov.w * scale;
        const h = w * (el.naturalHeight / el.naturalWidth);
        ctx.drawImage(el, ov.x * scale, ov.y * scale, w, h);
      }
      const blob: Blob = await new Promise((res) =>
        canvas.toBlob((b) => res(b!), 'image/jpeg', 0.85));
      const base = preview.att.filename.replace(/\.[^.]+$/, '');
      const file = new File([blob], `${base} (з печаткою).jpg`, { type: 'image/jpeg' });
      await attachments.upload({
        organization_id: organizationId, document_type: documentType,
        document_id: documentId!, file,
      });
      qc.invalidateQueries({ queryKey: ['attachments', documentType, documentId] });
      closePreview();
    } finally {
      setSaving(false);
    }
  };
  const isSaved = !!documentId && documentId !== 'new';

  // Контроль підписання первинки (0429): статус документа + поточна
  // позначка. Активний лише якщо опція увімкнена на орг і тип у списку.
  const scStatusQ = useQuery({
    queryKey: ['sign-control-statuses', documentType, organizationId, documentId],
    queryFn: () => signControl.statuses(organizationId, documentType, [documentId!]),
    enabled: isSaved && !!organizationId,
    staleTime: 30_000,
  });
  const scActive = !!scStatusQ.data?.enabled && !!scStatusQ.data?.controlled;
  const scStatus = scActive
    ? (scStatusQ.data?.statuses[documentId!] ?? null) : null;
  const scMarkQ = useQuery({
    queryKey: ['sign-control-mark', documentType, documentId],
    queryFn: () => api.get<{ mark: { kind: string; attachment_id: string | null } | null }>(
      '/sign-control/mark', { params: { organization_id: organizationId,
        document_type: documentType, document_id: documentId } },
    ).then((r) => r.data.mark),
    enabled: scActive,
  });
  const scRefresh = () => {
    qc.invalidateQueries({ queryKey: ['sign-control-statuses'] });
    qc.invalidateQueries({ queryKey: ['sign-control-mark', documentType, documentId] });
  };
  // Попап одразу після завантаження файла (рішення власника 2026-08-17):
  // «це підписаний документ?» → позначка SCAN знімає документ з контролю.
  const [scAsk, setScAsk] = useState<{ attachmentId: string; filename: string } | null>(null);
  const scMark = async (attachmentId: string) => {
    try {
      await signControl.mark(organizationId, {
        document_type: documentType, document_id: documentId!,
        kind: 'SCAN', attachment_id: attachmentId });
      scRefresh();
    } catch { window.alert('Не вдалося поставити позначку'); }
    setScAsk(null);
  };
  // «Паперовий оригінал» (2026-08-19): зняти з контролю БЕЗ скану — лише
  // коли режим увімкнено на картці орг (радіо у «списку документів
  // контролю») і документ ще не підписаний/не позначений.
  const scPaperEnabled = !!scStatusQ.data?.paper_original;
  const scMarkPaper = async () => {
    const ok = await confirm(
      'Позначити документ як паперовий оригінал (без скану) і зняти '
      + 'з контролю підписання?');
    if (!ok) return;
    try {
      await signControl.mark(organizationId, {
        document_type: documentType, document_id: documentId!,
        kind: 'PAPER' });
      scRefresh();
    } catch { window.alert('Не вдалося поставити позначку'); }
  };
  const scUnmark = async () => {
    try {
      await signControl.unmark(organizationId, documentType, documentId!);
      scRefresh();
    } catch { window.alert('Не вдалося зняти позначку'); }
  };

  const listQ = useQuery({
    queryKey: ['attachments', documentType, documentId],
    queryFn: () => attachments.list({
      organization_id: organizationId,
      document_type: documentType,
      document_id: documentId!,
    }),
    enabled: isSaved && !!organizationId,
  });

  const upload = useMutation({
    mutationFn: (file: File) => attachments.upload({
      organization_id: organizationId,
      document_type: documentType,
      document_id: documentId!,
      file,
    }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['attachments', documentType, documentId] });
      // 0429: одразу після завантаження — попап «зняти з контролю?»
      // (лише коли контроль активний і документ ще непідписаний).
      const att = (created as unknown as {
        data?: { id?: string; filename?: string } })?.data;
      if (scActive && scStatus === 'UNSIGNED' && att?.id) {
        setScAsk({ attachmentId: att.id, filename: att.filename ?? '' });
      }
    },
    onError: (e) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        .response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => (d as { msg?: string }).msg ?? '').join('; ')
          : 'Не вдалося завантажити файл';
      window.alert(msg || 'Не вдалося завантажити файл');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => attachments.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['attachments', documentType, documentId] }),
  });

  const queueToJournal = async (a: Attachment) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', organizationId);
      fd.append('document_type', documentType);
      fd.append('document_id', documentId!);
      fd.append('attachment_id', a.id);
      const r = await api.post<{ existing: boolean }>(
        '/edo/queue', fd, { headers: { 'X-Silent-422': '1' } });
      qc.invalidateQueries({ queryKey: ['edo-journal'] });
      window.alert(r.data.existing
        ? 'Документ уже у Журналі ЕДО (не відправлено)'
        : 'Додано до Журналу ЕДО — відправити можна зі сторінки журналу');
    } catch (e) {
      const d = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      window.alert(typeof d === 'string' ? d : 'Не вдалося додати до журналу');
    } finally {
      setSending(false);
    }
  };

  const downloadFile = async (a: Attachment) => {
    const blob = await attachments.downloadBlob(a.id);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  // НОВИЙ (несохранений) документ: панель ВИДНО, кнопка неактивна з
  // підказкою (зауваження власника 2026-08-14 — не ховати функцію).
  const rows: Attachment[] = isSaved ? (listQ.data ?? []) : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        {/* ч.851: підпис — як у «Коментар» (label), без емодзі. */}
        <label style={{ margin: 0 }}>
          Вкладення{rows.length > 0 ? ` (${rows.length})` : ''}
        </label>
        {isSaved && (
          <span style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>
            PDF, JPEG, PNG, HEIC · до 10 МБ
          </span>
        )}
        {!isSaved && (
          <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
            стане доступно після збереження документа
          </span>
        )}
        {/* ч.852: «паперовий оригінал» лишається (зауваження власника п.4). */}
        {scActive && scPaperEnabled && scStatus === 'UNSIGNED' && (
          <button onClick={() => void scMarkPaper()} className="btn-green"
            title="Зняти з контролю підписання без скану — паперовий оригінал у наявності">
            паперовий оригінал
          </button>
        )}
        {/* ч.852: «додати файл» — ПРАВОРУЧ у рядку назви. */}
        <button onClick={() => fileRef.current?.click()}
          className="btn-doc" style={{ marginLeft: 'auto' }}
          disabled={!isSaved || upload.isPending}
          title={isSaved ? undefined
            : 'Спершу збережіть документ — вкладення привʼязуються до збереженого документа'}>
          <IconPaperclip />{upload.isPending ? 'завантаження…' : 'додати файл'}
        </button>
        <input ref={fileRef} type="file" hidden
          accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = '';
          }} />
      </div>
      {rows.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0.5rem 0 0', padding: 0 }}>
          {rows.map((a) => (
            <li key={a.id} className="docfile">
              <a href="#" onClick={(e) => { e.preventDefault(); void openPreview(a); }}
                className="docfile__name">
                {a.filename}
              </a>
              {/* ч.851 (рішення власника): розмір і «(стиснуто)» у рядку не показуємо. */}
              {(() => {
                const e = edoByAtt.get(a.id);
                if (!e) return null;
                return (
                  <span style={{ display: 'inline-flex', gap: '0.35rem',
                    alignItems: 'center', flex: '0 0 auto' }}>
                    <span title="Документ у процесі ЕЦП-підписання (журнал ЕДО)"
                      style={{ color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
                        borderRadius: 4, fontSize: '0.7rem', fontWeight: 700,
                        padding: '0 4px' }}>
                      ЕЦП
                    </span>
                    {e.sign_status === 'SIGNED' ? (
                      <>
                        <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600,
                          fontSize: '0.75rem' }} title={e.signer_info ?? undefined}>
                          підписано контрагентом
                        </span>
                        <button style={{ padding: '0.1rem 0.45rem', fontSize: '0.75rem' }}
                          title="Скачати підписаний документ (PDF / p7s / ZIP з квитанцією)"
                          onClick={() => setDlEdo({ row: e, base: a.filename.replace(/\.[^.]+$/, '') })}>
                          скачати
                        </button>
                      </>
                    ) : e.sign_status === 'WAITING' ? (
                      <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600,
                        fontSize: '0.75rem' }}>очікуємо підпису</span>
                    ) : (
                      <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600,
                        fontSize: '0.75rem' }} title="Підписано вашим КЕП">
                        ✓ підписано
                      </span>
                    )}
                  </span>
                );
              })()}
              {/* 0429: позначка «підписаний документ» у рядку вкладення. */}
              {/* ч.855: без «?»; позначений як підписаний — ЗЕЛЕНА кнопка. */}
              {scActive && scMarkQ.data?.attachment_id === a.id ? (
                <button className="btn-green" style={{ padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
                  title="Позначено як підписаний документ — натисніть, щоб повернути на контроль підписання"
                  onClick={() => { void scUnmark(); }}>
                  ✓ підписаний
                </button>
              ) : scActive && scStatus === 'UNSIGNED' ? (
                <button style={{ padding: '0.1rem 0.45rem', fontSize: '0.75rem' }}
                  title="Позначити цей файл підписаним документом (зняти з контролю підписання)"
                  onClick={() => { void scMark(a.id); }}>
                  підписаний
                </button>
              ) : null}
              <button className="btn-icon" disabled={sending || !canEdoSend}
                title={canEdoSend ? 'Додати документ до Журналу ЕДО (відправка — зі сторінки журналу)'
                  : 'Недоступно: у вашій ролі немає права «ЕДО: надсилання документів контрагенту»'}
                onClick={() => void queueToJournal(a)}>
                <IconSend />
              </button>
              <button className="btn-icon"
                title="Завантажити файл"
                onClick={() => void downloadFile(a)}>
                <IconDownload />
              </button>
              <button className="danger" style={{ padding: '0.1rem 0.45rem' }}
                title="Видалити вкладення"
                onClick={() => confirm(`Видалити файл «${a.filename}»?`)
                  .then((ok) => { if (ok) remove.mutate(a.id); })}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {scAsk && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setScAsk(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(480px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Зняти документ з контролю підписання?</h3>
            <p style={{ fontSize: '0.85rem', margin: '0 0 0.7rem' }}>
              Якщо файл <b>{scAsk.filename}</b> — це підписаний примірник
              документа, позначте його, і документ буде знято з контролю
              підписання.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setScAsk(null)}>ні, звичайний файл</button>
              <button className="primary"
                onClick={() => { void scMark(scAsk.attachmentId); }}>
                так, це підписаний документ
              </button>
            </div>
          </div>
        </div>
      )}
      {dlEdo && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDlEdo(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(480px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Скачати підписаний документ</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.7rem' }}>
              <b>{dlEdo.base}</b> — оберіть формат:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <button onClick={() => void downloadEdoFormat(dlEdo.row, dlEdo.base, 'pdf')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>Документ</b> — лише сам файл (PDF/зображення)
              </button>
              <button onClick={() => void downloadEdoFormat(dlEdo.row, dlEdo.base, 'p7s')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>Підпис (.p7s)</b> — лише файл підпису контрагента
              </button>
              <button onClick={() => void downloadEdoFormat(dlEdo.row, dlEdo.base, 'zip')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>ZIP-архів</b> — документ + підписи (.p7s) відправника і
                контрагента + квитанція + інструкція перевірки
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.7rem' }}>
              <button onClick={() => setDlEdo(null)}>закрити</button>
            </div>
          </div>
        </div>
      )}
      {preview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={closePreview}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 8, width: 'min(920px, 94vw)',
            height: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.6rem 0.9rem', borderBottom: '1px solid var(--border)',
            }}>
              <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {preview.att.filename}
              </strong>
              {hasStamps && stampButtons.map((b) => (
                <button key={b.kind}
                  onClick={() => {
                    if (preview.att.mime_type === 'application/pdf' && !editMode) {
                      setEditMode(true);
                    }
                    void addOverlay(b.kind, b.list);
                  }}
                  title="Перетягніть штамп мишею на потрібне місце">
                  {b.label}
                </button>
              ))}
              {editMode && overlays.length > 0 && (
                <button className="primary" onClick={() => void saveStamped()}
                  disabled={saving}
                  title="Зберегти копію документа з накладеною печаткою/підписом (окремим вкладенням, оригінал лишається)">
                  {saving ? 'збереження…' : 'зберегти з печаткою'}
                </button>
              )}
              <a href={preview.url} download={preview.att.filename}>
                <button>завантажити</button>
              </a>
              <button onClick={() => frameRef.current?.contentWindow?.print()}>
                друкувати
              </button>
              <button onClick={closePreview} title="Закрити">✕</button>
            </div>
            {preview.att.mime_type === 'application/pdf' && editMode ? (
              <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-soft)',
                borderRadius: '0 0 8px 8px', display: 'flex',
                flexDirection: 'column', alignItems: 'center' }}
                onPointerMove={onDragMove} onPointerUp={onDragEnd}>
                {pdfPages > 1 && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
                    padding: '0.35rem' }}>
                    <button disabled={pdfPage <= 1}
                      onClick={() => setPdfPage((p) => p - 1)}>← попередня</button>
                    <span style={{ fontSize: '0.85rem' }}>
                      сторінка {pdfPage} з {pdfPages}
                    </span>
                    <button disabled={pdfPage >= pdfPages}
                      onClick={() => setPdfPage((p) => p + 1)}>наступна →</button>
                  </div>
                )}
                <div style={{ position: 'relative', alignSelf: 'center' }}>
                  {pdfPageUrl
                    ? <img ref={docImgRef} src={pdfPageUrl} alt={`стор. ${pdfPage}`}
                        style={{ maxWidth: '100%', height: 'auto', display: 'block',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }} />
                    : <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Рендер сторінки…</div>}
                  {overlays.filter((ov) => ov.page === pdfPage).map((ov) => {
                    const i = overlays.indexOf(ov);
                    return (
                    <div key={i} style={{
                      position: 'absolute', left: ov.x, top: ov.y, width: ov.w,
                      cursor: 'move', border: '1px dashed rgba(37,99,235,0.6)',
                      touchAction: 'none',
                    }}
                      onPointerDown={(e) => onDragStart(i, e)}>
                      <img src={ov.url} alt="" draggable={false}
                        style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />
                      <div style={{ position: 'absolute', top: -22, right: 0,
                        display: 'flex', gap: 2 }}>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.map((x, j) =>
                            j === i ? { ...x, w: Math.max(50, x.w - 20) } : x))}>−</button>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.map((x, j) =>
                            j === i ? { ...x, w: Math.min(600, x.w + 20) } : x))}>+</button>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.filter((_, j) => j !== i))}>×</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : preview.att.mime_type === 'application/pdf' ? (
              <iframe ref={frameRef} src={preview.url} title={preview.att.filename}
                style={{ flex: 1, border: 0, borderRadius: '0 0 8px 8px' }} />
            ) : (
              /* Зображення: контейнер з оверлеями печатки/підпису (drag). */
              <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-soft)',
                borderRadius: '0 0 8px 8px', display: 'flex',
                justifyContent: 'center' }}
                onPointerMove={onDragMove} onPointerUp={onDragEnd}>
                <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
                  <img ref={docImgRef} src={preview.url} alt={preview.att.filename}
                    style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
                  {overlays.map((ov, i) => (
                    <div key={i} style={{
                      position: 'absolute', left: ov.x, top: ov.y, width: ov.w,
                      cursor: 'move', border: '1px dashed rgba(37,99,235,0.6)',
                      touchAction: 'none',
                    }}
                      onPointerDown={(e) => onDragStart(i, e)}>
                      <img src={ov.url} alt="" draggable={false}
                        style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />
                      <div style={{ position: 'absolute', top: -22, right: 0,
                        display: 'flex', gap: 2 }}>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.map((x, j) =>
                            j === i ? { ...x, w: Math.max(50, x.w - 20) } : x))}>−</button>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.map((x, j) =>
                            j === i ? { ...x, w: Math.min(600, x.w + 20) } : x))}>+</button>
                        <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setOverlays((o) => o.filter((_, j) => j !== i))}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
